import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import ScrollBasic from '../components/shared/ScrollBasic';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { Profile } from 'src/replicant/profile';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import platform from '@play-co/gcinstant';
import StateObserver from 'src/StateObserver';
import { profileAvatarAssets } from 'src/replicant/creatives/assets';

const ui = uiConfig.buruburu.profile;

type AvatarItem = { url: string };
type AvatarRow = [AvatarItem, AvatarItem?, AvatarItem?];

export default class PopupAvatarSelection extends PopupBasic<
  (selection: string) => void
> {
  private scroll: ScrollBasic<AvatarRow>;
  private originalSelection: string;
  private currentSelection: string;
  private items: View[] = [];
  private okButton: ButtonScaleViewWithText;

  constructor(opts: { superview: View; close: (selection: string) => void }) {
    super({
      superview: opts.superview,
      close: () => opts.close(this.originalSelection),
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 835,
      darkerBg: false,
      tracking: { feature: 'profile', $subFeature: 'popup-editProfile-avatar' },
    });

    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      visible: false,
    });

    this.scroll = new ScrollBasic<AvatarRow>({
      superview: this.box,
      createItem: this.createItem.bind(this),
      showBg: true,
      margin: 15,
      rect: {
        x: (this.box.style.width - 512) * 0.5,
        y: 94,
        width: 512,
        height: 586,
      },
    });

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      ...ui.button,
      superview: this.box,
      x: this.box.style.width / 4,
      y: this.box.style.height - 25 - 100 / 2,
      localeText: () => i18n('basic.cancel'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close(this.originalSelection);
      },
      tracking: {
        feature: 'profile',
        $subFeature: 'popup-editProfile-avatar-close',
      },
    });

    this.okButton = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      ...ui.button,
      x: (this.box.style.width / 4) * 3,
      y: this.box.style.height - 25 - 100 / 2,
      localeText: () => i18n('basic.ok'),
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close(this.currentSelection);
      },
      tracking: {
        feature: 'profile',
        $subFeature: 'popup-editProfile-avatar-ok',
      },
    });
  }

  public init({ currentAvatar }: { currentAvatar: string }) {
    super.init({
      title: i18n('profile.edit.imageTitle'),
    });

    this.originalSelection = currentAvatar;
    this.currentSelection = currentAvatar;
    this.scroll.setItems(
      [
        // { url: platform.playerPhoto },
        ...Object.keys(profileAvatarAssets).map((id) => ({
          url: StateObserver.replicant.getChatbotAssetUrl(id),
        })),
      ].reduce((result, _, i, array) => {
        if (i % 3 === 0) {
          result.push(array.slice(i, i + 3));
        }
        return result;
      }, []),
    );

    this.updateSelection();
  }

  private createItem(superview: View, _: number, avatarItems: AvatarRow) {
    const row = superview.addSubview(
      new FlexBoxView({
        width: superview.style.width,
        height: ui.edit.avatarSize,
        flex: {
          justifyContent: 'space-between',
          paddingHorizontal: 20,
        },
      }),
    );

    avatarItems.forEach(({ url }) => {
      const frame = new View({
        ...ui.edit.avatarOutline,
        tag: url,
        superview: row,
      });

      new ButtonScaleView({
        ...ui.edit.avatarButton,
        superview: frame,
        image: url,
        onClick: async () => {
          this.currentSelection = url;
          this.updateSelection();
        },
        tracking: {
          feature: 'profile',
          $subFeature: 'popup-editProfile-avatar-selected',
        },
      });

      this.items.push(frame);
    });

    return row;
  }

  private updateSelection() {
    this.okButton.setDisabled(!this.currentSelection);
    this.items.forEach((item) => {
      item.style.backgroundColor =
        item.tag === this.currentSelection ? '#79c553' : '#ffffff';
    });
  }
}
